import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { api, imgUrl, money } from "@/lib/api";
import { KIDDEE } from "@/config/sections";
import { HOME } from "@/constants/testIds";

const BUILT_IN = { id: "built-in", video: KIDDEE.video, poster: "/media/kiddee-video-poster.jpg?v=2", title: "Kiddee" };

export const ClipPlayer = () => {
  const [clips, setClips] = useState([BUILT_IN]);
  const [i, setI] = useState(0);
  const ref = useRef(null);
  const { add } = useCart();

  useEffect(() => {
    api.get("/clips").then(({ data }) => setClips([BUILT_IN, ...data.map((c) => ({ ...c, video: imgUrl(c.video), poster: imgUrl(c.poster) }))])).catch(() => {});
  }, []);

  useEffect(() => {
    ref.current?.play?.().catch(() => {});
  }, [i]);

  const clip = clips[i] || BUILT_IN;
  const many = clips.length > 1;
  return (
    <div className="col-span-2 sm:col-span-4 relative">
      <video
        ref={ref}
        key={clip.id}
        data-testid={HOME.kiddeeVideo}
        src={clip.video}
        poster={clip.poster}
        preload="auto"
        autoPlay
        muted
        loop={!many}
        playsInline
        controls
        onEnded={() => setI((n) => (n + 1) % clips.length)}
        className="w-full aspect-[3/2] object-contain rounded-3xl bg-[#1C1917]"
      />
      {clip.product && (
        <div key={`shop-${clip.id}`} className="absolute bottom-16 left-3 sm:left-4 flex items-center gap-1 bg-white/95 backdrop-blur-md rounded-full p-1.5 shadow-lg fade-up">
          <Link to={`/product/${clip.product.slug}`} data-testid={HOME.kiddeeShopToy} className="flex items-center gap-3 rounded-full pr-2 hover:opacity-80 transition-opacity">
            <img src={imgUrl(clip.product.image)} alt="" className="w-10 h-10 rounded-full object-cover" />
            <span className="text-left leading-tight">
              <span className="flex items-center gap-1.5 text-sm font-semibold"><ShoppingBag size={14} className="text-[#E06D44]" /> Shop this toy</span>
              <span className="block text-xs text-stone-600 max-w-[130px] sm:max-w-[240px] truncate">{clip.product.name} · {money(clip.product.price)}</span>
            </span>
          </Link>
          <button
            type="button"
            data-testid={HOME.kiddeeAddToCart}
            onClick={() => add(clip.product)}
            aria-label={`Add ${clip.product.name} to cart`}
            className="flex items-center gap-1 h-10 pl-3 pr-4 rounded-full bg-[#E06D44] text-white text-sm font-semibold hover:bg-[#C95B33] active:scale-95 transition-[background-color,transform]"
          >
            <Plus size={15} /> Add
          </button>
        </div>
      )}
      {many && (
        <div className="absolute top-3 right-3 flex bg-black/40 backdrop-blur-md rounded-full px-1">
          {clips.map((c, n) => (
            <button
              key={c.id}
              onClick={() => setI(n)}
              aria-label={`Play clip ${n + 1}${c.title ? `: ${c.title}` : ""}`}
              data-testid={HOME.kiddeeClipDot(n)}
              className="group h-11 px-1.5 grid place-items-center"
            >
              <span className={`block h-2 rounded-full transition-[width,background-color] duration-300 ${n === i ? "w-6 bg-white" : "w-2 bg-white/50 group-hover:bg-white/80"}`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
